import { MenuItem, Menubar } from '@agro1desenvolvimento/react-components';
import { useImmutableCallback } from '@agro1desenvolvimento/react-hooks';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { chain } from 'lodash';
import { faArrowRotateRight, faBell } from '@fortawesome/free-solid-svg-icons';
import logo from './aqila-branco.png';
import useLocalStore from '../../hooks/use-local-store';
import navigateToAqila1 from '../../utils/navigate-to-aqila1';
import PermissoesService from '../../services/permissoes';
import PropriedadesService from '../../services/propriedades';
import AuthSession from '../../services/auth-session';
import { Propriedade } from '../../@types';
import BadgeOverlay, { BadgeOverlayRef } from '../BadgeOverlay/BadgeOverlay';
import BadgeButton from '../BadgeButton/BadgeButton';
import useIntegrationErrors from '../../features/integration-errors/use-integration-errors';
import usePendingApproves from '../../features/pending-aproves/use-pending-approves';
import useElementHeightCssVar from '../../hooks/element-height-in-css-var';
import IntegrationErrorsPanelContent from '../../features/integration-errors/IntegrationErrorsPanel';
import PendingAprovePanelContent from '../../features/pending-aproves/PendingAprovesPanel';
import { enableRelatorioVisitasV2, enableV2Menu } from './enabled-menus-temp';

const ROUTES_TO_HIDE_DROPDOWN_PROPRIEDADES = [
  '/recomendacoes-semeadura/create',
  '/recomendacoes-semeadura/edit/.*',
  '/tratamento-sementes/create',
  '/tratamento-sementes/edit/.*',
  '/gerenciador-dados',
  '/gerenciador-dados/.*',
];

const AppMenuBar = () => {
  const navigate = useNavigate();
  const currentRoute = useLocation().pathname;
  const user = useLocalStore('user');
  const currentPropriedade = useLocalStore('currentPropriedade');
  const [propriedades, setPropriedades] = useState<Propriedade[]>([]);
  const permissoes = PermissoesService;
  const { errorsCount } = useIntegrationErrors();
  const { notificationsCount } = usePendingApproves();

  const integrationErrorsBadgeRef = useRef<BadgeOverlayRef>(null);

  const pendingAproveBadgeRef = useRef<BadgeOverlayRef>(null);

  const menuBarContainerRef = useElementHeightCssVar<HTMLDivElement>(
    '--app-menubar-height',
  );

  const propriedadesDropdownClassName = useMemo(() => {
    const shouldHide = !!ROUTES_TO_HIDE_DROPDOWN_PROPRIEDADES.find((route) =>
      currentRoute.match(route),
    );

    return shouldHide ? 'hidden' : '';
  }, [currentRoute]);

  const getOptionClassName = useCallback(
    ({ shouldBeShown = true, path }: GetOptionClassNameParams = {}) => {
      let className = '';

      if (!shouldBeShown) {
        className += 'hidden';
      } else if (path && matchPath({ path }, currentRoute)) {
        className += 'p-focus';
      }

      return className;
    },
    [currentRoute],
  );

  const propriedadeAtual = useMemo(
    () => currentPropriedade.value?.descricao ?? 'Não selecionado',
    [currentPropriedade.value],
  );

  const propriedadesDropDown = useMemo(
    () =>
      chain(propriedades)
        .filter(
          (propriedade) => propriedade.id !== currentPropriedade.value?.id,
        )
        .sortBy('descricao')
        .map((propriedade) => ({
          label: propriedade.descricao,
          command: () => currentPropriedade.setValue(propriedade),
        }))
        .value(),
    [propriedades, currentPropriedade],
  );

  const handleIntegrationErrorsClick = useImmutableCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      integrationErrorsBadgeRef.current?.toggleOverlay(event);
    },
  );

  const handlePendingApprovesClick = useImmutableCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      pendingAproveBadgeRef.current?.toggleOverlay(event);
    },
  );

  const integrationErrorsTemplate = useImmutableCallback(() => (
    <BadgeButton
      count={errorsCount}
      onClick={handleIntegrationErrorsClick}
      icon={faArrowRotateRight}
    />
  ));

  const pendingAprovalTemplate = useImmutableCallback(() => (
    <BadgeButton
      count={notificationsCount}
      onClick={handlePendingApprovesClick}
      icon={faBell}
      variant="pending"
    />
  ));

  const start = (
    <a href="/#!/">
      <img src={logo} alt="Aqila" className="menubar-img" />
    </a>
  );

  const items: MenuItem[] = [
    {
      label: 'Propriedades (Aqila 2)',
      command: () => navigate('/'),
      // Hotfix temporário.
      // Esse menu está desabilitado completamente até termos a feature flag (WEM-1862) e terminarmos a tela completamente.
      className: getOptionClassName({ shouldBeShown: false, path: '/' }),
    },
    {
      label: 'Propriedades',
      command: () => navigateToAqila1(),
      className: getOptionClassName(),
    },
    {
      label: 'Visita de Campo',
      items: [
        {
          label: 'Painel',
          command: () => navigateToAqila1('propriedade'),
          className: getOptionClassName({
            shouldBeShown: permissoes.visitsPanel.read,
          }),
        },
        {
          label: 'Dashboard',
          command: () => navigateToAqila1('dashboard'),
          className: getOptionClassName({
            shouldBeShown: permissoes.dashboard.read,
          }),
        },
        {
          label: 'Relatórios',
          className: getOptionClassName({
            shouldBeShown:
              permissoes.visitsReport.read || permissoes.timeline.read,
          }),
          items: [
            {
              label: 'Relatório de visita',
              command: () => navigateToAqila1('relatorios'),
              className: getOptionClassName({
                shouldBeShown: permissoes.visitsReport.read,
              }),
            },
            {
              label: 'Relatório de visita (Aqila 2)',
              command: () => navigate('/relatorio-visita'),
              className: getOptionClassName({
                shouldBeShown:
                  permissoes.visitsReport.read && enableRelatorioVisitasV2,
              }),
            },
            {
              label: 'Relatório de fechamento',
              command: () => navigateToAqila1('relatoriofechamento'),
              className: getOptionClassName({
                shouldBeShown: permissoes.timeline.read,
              }),
            },
            {
              label: 'Exportador',
              command: () => navigateToAqila1('exportador'),
              className: getOptionClassName({
                shouldBeShown: permissoes.timeline.read,
              }),
            },
          ],
        },
        {
          label: 'Timeline',
          command: () => navigateToAqila1('timeline'),
          className: getOptionClassName({
            shouldBeShown: permissoes.timeline.read,
          }),
        },
        {
          label: 'Aplicações',
          command: () => navigateToAqila1('recomendado_aplicado'),
          className: getOptionClassName({
            shouldBeShown:
              permissoes.recomendacao.read && permissoes.pulverizacao.read,
          }),
        },
        {
          label: 'Produtividade',
          command: () => navigateToAqila1('produtividade'),
          className: getOptionClassName({
            shouldBeShown: permissoes.produtividade.read,
          }),
        },
        {
          label: 'Recomendações de Semeadura',
          command: () => navigateToAqila1('recomendacoes_semeadura'),
          className: getOptionClassName({
            shouldBeShown:
              permissoes.recomendacaoSemeadura.read ||
              permissoes.recomendacaoSemeadura.manage,
          }),
        },
        {
          label: 'Recomendações de Semeadura (Aqila 2)',
          command: () => navigate('recomendacoes-semeadura'),
          className: getOptionClassName({
            shouldBeShown:
              (permissoes.recomendacaoSemeadura.read ||
                permissoes.recomendacaoSemeadura.manage ||
                permissoes.recomendacaoSemeadura.editDelete) &&
              enableV2Menu,
          }),
        },
        {
          label: 'Tratamento de Sementes (Aqila 2)',
          command: () => navigate('tratamento-sementes'),
          className: getOptionClassName({
            shouldBeShown:
              (permissoes.tratamentoSemente.read ||
                permissoes.tratamentoSemente.manage) &&
              enableV2Menu,
          }),
        },
        {
          label: 'Cronogramas de Fungicidas',
          command: () => navigateToAqila1('cronograma-fungicidas'),
          className: getOptionClassName({
            shouldBeShown: permissoes.recomendacao.read,
          }),
        },
        {
          label: 'Contratos',
          command: () => navigateToAqila1('contratos'),
          className: getOptionClassName({
            shouldBeShown: permissoes.contratos.manage,
          }),
        },
        {
          label: 'Pendências',
          command: () => navigateToAqila1('pendencias'),
          className: getOptionClassName({
            shouldBeShown: permissoes.tarefa.read,
          }),
        },
      ],
    },
    {
      label: 'Ocorrências de Máquinas',
      command: () => navigateToAqila1('ocorrencias_maquinas'),
      className: getOptionClassName({
        shouldBeShown: permissoes.ocorrenciaComMaquinas.read,
        path: 'ocorrencias_maquinas',
      }),
    },
    {
      label: 'Linha do Tempo',
      command: () => navigate('linha-tempo'),
      className: getOptionClassName({
        shouldBeShown: false,
        path: 'linha-tempo',
      }),
    },
    {
      template: integrationErrorsTemplate,
      className: getOptionClassName({ shouldBeShown: false }),
    },
    {
      template: pendingAprovalTemplate,
      className: getOptionClassName({ shouldBeShown: false }),
    },
    {
      icon: 'pi pi-map-marker',
      label: propriedadeAtual,
      items: propriedadesDropDown,
      className: propriedadesDropdownClassName,
    },
    {
      icon: 'pi pi-user',
      label: user?.value?.nome,
      items: [
        {
          label: 'Aplicativo',
          command: () => navigateToAqila1('app'),
        },
        {
          label: 'Gerenciador de dados',
          // O Gerenciador de dados da V2 ainda não substitui o da V1
          command: () => navigateToAqila1('gerenciador_dados'),
        },
        {
          label: 'Configurações',
          command: () => navigateToAqila1('parametro'),
          className: getOptionClassName({
            shouldBeShown: permissoes.parameters.manage,
          }),
        },
        {
          label: 'Sair',
          command: () => {
            AuthSession.logoutAndRedirectToLogin();
          },
        },
      ],
    },
  ];

  useEffect(() => {
    const loadData = async () => {
      try {
        setPropriedades(await PropriedadesService.list());
      } catch (error) {
        console.error(error);
      }
    };

    loadData();
  }, []);

  return (
    <>
      <div ref={menuBarContainerRef}>
        <Menubar start={start} model={items} className="py-0 w-full" />
      </div>
      <BadgeOverlay ref={integrationErrorsBadgeRef}>
        <IntegrationErrorsPanelContent />
      </BadgeOverlay>
      <BadgeOverlay ref={pendingAproveBadgeRef}>
        <PendingAprovePanelContent />
      </BadgeOverlay>
    </>
  );
};

type GetOptionClassNameParams = {
  shouldBeShown?: boolean;
  path?: string;
};

export default AppMenuBar;
